import { statThresholds } from './thresholds';
import type { ProportionStatInput, StatTestResult } from './types';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export {
  DEFAULT_SIGNIFICANCE_ALPHA,
  SIGNIFICANCE_CONFIDENCE_LEVELS,
  statThresholds,
} from './thresholds';
export type { ProportionStatGroup, ProportionStatInput, StatTestResult } from './types';

export function computeProportionStatTest(
  input: ProportionStatInput,
  alpha: number = statThresholds.alpha
): null | StatTestResult {
  const { lowBaseSize } = statThresholds;
  const { groups } = input;
  if (groups.length < 2) return null;

  const zCrit = zCriticalForAlpha(alpha);

  const eligibleIdx: number[] = [];
  groups.forEach((g, i) => {
    if (g.n >= lowBaseSize && g.options.length > 0) eligibleIdx.push(i);
  });
  if (eligibleIdx.length < 2) return null;

  const columnLetters: string[] = groups.map((_, i) => indexToColumnLetter(i));

  // Row count is the longest eligible group so no genuinely-significant cell in
  // a longer group is left untested; per-cell lookups guard against ragged rows
  // so a shorter group can't index past its options.
  const numRows = Math.max(...eligibleIdx.map((gi) => groups[gi].options.length));
  const cellHigherThan: string[][][] = [];

  for (let r = 0; r < numRows; r++) {
    const rowCells: string[][] = groups.map(() => []);
    for (const gi of eligibleIdx) {
      for (const gj of eligibleIdx) {
        if (gi === gj) continue;
        const optI = groups[gi].options[r];
        const optJ = groups[gj].options[r];
        if (!optI || !optJ) continue;
        // Suppressed/withheld cell (null count) can't be compared — skip the pair.
        if (optI.count == null || optJ.count == null) continue;
        const n1 = groups[gi].n;
        const n2 = groups[gj].n;
        // Malformed cell (count outside [0, n]) can't be a valid proportion — skip the pair.
        if (optI.count < 0 || optI.count > n1 || optJ.count < 0 || optJ.count > n2) continue;
        const p1 = n1 === 0 ? 0 : optI.count / n1;
        const p2 = n2 === 0 ? 0 : optJ.count / n2;
        if (p1 <= p2) continue;
        if (isSignificant(p1, n1, optI.count, p2, n2, optJ.count, zCrit)) {
          rowCells[gi].push(columnLetters[gj]);
        }
      }
    }
    cellHigherThan.push(rowCells);
  }

  return { cellHigherThan, columnLetters };
}

/**
 * Build the column letter for the n-th column (display order).
 *
 *   0  -> 'A'   25 -> 'Z'   26 -> 'AA'   27 -> 'AB'
 *
 * Report compare-by rarely exceeds 26 columns, but bijective base-26 keeps
 * producing letters rather than collapsing past 'Z'.
 */
export function indexToColumnLetter(index: number): string {
  if (!Number.isInteger(index) || index < 0) return '';
  if (index < 26) return LETTERS[index];
  let n = index;
  let out = '';
  while (n >= 0) {
    out = LETTERS[n % 26] + out;
    n = Math.floor(n / 26) - 1;
  }
  return out;
}

/**
 * One-tailed z critical value for a given alpha, via the Beasley-Springer-Moro
 * rational approximation to the inverse normal CDF (~1e-9 for alpha 0.01..0.20).
 */
export function zCriticalForAlpha(alpha: number): number {
  // P(Z > z) = alpha  =>  z = inverseCdf(1 - alpha).
  return inverseStandardNormalCdf(1 - alpha);
}

function inverseStandardNormalCdf(p: number): number {
  if (!(p > 0 && p < 1)) {
    throw new Error(`inverseStandardNormalCdf: p must be in (0, 1), got ${p}`);
  }
  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2,
    -3.066479806614716e1, 2.506628277459239,
  ];
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1,
    -1.328068155288572e1,
  ];
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734,
    4.374664141464968, 2.938163982698783,
  ];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
  const pLow = 0.02425;
  const pHigh = 1 - pLow;

  let q: number;
  let r: number;
  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
  if (p <= pHigh) {
    q = p - 0.5;
    r = q * q;
    return (
      ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
    );
  }
  q = Math.sqrt(-2 * Math.log(1 - p));
  return (
    -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
    ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  );
}

function isSignificant(
  p1: number,
  n1: number,
  x1: number,
  p2: number,
  n2: number,
  x2: number,
  zCrit: number
): boolean {
  const pHat = (x1 + x2) / (n1 + n2);
  const variance = pHat * (1 - pHat) * (1 / n1 + 1 / n2);
  if (variance <= 0) return false;
  // Already filtered to p1 > p2, so z is positive — one-tailed.
  const z = (p1 - p2) / Math.sqrt(variance);
  return z > zCrit;
}
